import { supabase } from '@/lib/supabase'
import type { Tables } from '@/types/database.types'

export type Booking = Tables<'bookings'>
export type BookingType = 'flight' | 'hotel' | 'transport'

export interface NewBookingInput {
  tripId: string
  type: BookingType
  reference?: string
  startAt?: string
  endAt?: string
}

export interface BookingPatch {
  type: BookingType
  reference?: string
  startAt?: string
  endAt?: string
}

export async function listBookings(tripId: string): Promise<Booking[]> {
  const { data, error } = await supabase
    .from('bookings')
    .select('*')
    .eq('trip_id', tripId)
    .order('start_at', { nullsFirst: false })
  if (error) throw error
  return data
}

export async function createBooking(input: NewBookingInput): Promise<Booking> {
  const { data, error } = await supabase
    .from('bookings')
    .insert({
      trip_id: input.tripId,
      type: input.type,
      reference: input.reference,
      start_at: input.startAt,
      end_at: input.endAt,
    })
    .select()
    .single()
  if (error) throw error
  return data
}

export async function updateBooking(bookingId: string, patch: BookingPatch): Promise<Booking> {
  const { data, error } = await supabase
    .from('bookings')
    .update({
      type: patch.type,
      reference: patch.reference ?? null,
      start_at: patch.startAt ?? null,
      end_at: patch.endAt ?? null,
    })
    .eq('id', bookingId)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function deleteBooking(bookingId: string): Promise<void> {
  const { error } = await supabase.from('bookings').delete().eq('id', bookingId)
  if (error) throw error
}

export async function uploadBookingAttachment(
  tripId: string,
  bookingId: string,
  file: File,
): Promise<Booking> {
  const path = `${tripId}/${bookingId}-${file.name}`
  const { error: uploadError } = await supabase.storage
    .from('booking-attachments')
    .upload(path, file, { upsert: true })
  if (uploadError) throw uploadError

  const { data, error } = await supabase
    .from('bookings')
    .update({ attachment_path: path })
    .eq('id', bookingId)
    .select()
    .single()
  if (error) throw error
  return data
}

export async function getBookingAttachmentUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('booking-attachments')
    .createSignedUrl(path, 60 * 10)
  if (error) throw error
  return data.signedUrl
}

export async function deleteBookingAttachment(
  bookingId: string,
  attachmentPath: string,
): Promise<Booking> {
  const { error: removeError } = await supabase.storage
    .from('booking-attachments')
    .remove([attachmentPath])
  if (removeError) throw removeError

  const { data, error } = await supabase
    .from('bookings')
    .update({ attachment_path: null })
    .eq('id', bookingId)
    .select()
    .single()
  if (error) throw error
  return data
}
